/**
 * WallSketch metadata-preserving merge helpers.
 *
 * Centralizes the "when two nodes / two edges combine, what fields
 * carry forward" rules so every call site (auto-pipeline, drag-merge,
 * collinear dissolve, degree-2 dissolve) propagates the same data.
 *
 * The rules:
 *  - `roomIds` → union (deduped). A skeleton edge or node that touches
 *    rooms A and B continues to touch A and B after a merge with a
 *    sibling that touched B and C; the result touches A, B, C.
 *  - `name` → kept from `keep`. Display label; caller can override
 *    after merging if a fresh name is wanted.
 *  - `metadata` → merged as JSON when both sides are JSON objects;
 *    otherwise `keep.metadata ?? drop.metadata` (first non-null wins).
 *    Free-form string blobs aren't safe to concatenate, so the
 *    fallback avoids surprising mixed-content strings.
 *  - Edge-specific (`leftSize`, `rightSize`, `wallType`) → kept from
 *    `keep`. These are physical wall measurements bound to the
 *    surviving edge's geometry, not metadata that "adds up".
 *
 * Each helper returns a NEW object — callers spread it into a new id
 * + new name, etc. We don't mutate in place because the skeleton data
 * model is replaced atomically via `setData`.
 */

import type { SkeletonEdgeData, SkeletonNodeData } from '@pointorama/potree-shared';

/** Union two id lists, preserving order from `a` first then `b`. */
function unionIds(a: readonly string[], b: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of a) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  for (const id of b) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/**
 * Merge two metadata blobs. Both sides are `string | null | undefined`
 * carrying serialized JSON in normal use. Strategy:
 *  - Both null/undefined → null
 *  - One side null → the other (passes through opaque strings cleanly)
 *  - Both parse as JSON objects → shallow `{ ...drop, ...keep }` so
 *    `keep` wins on conflicts but `drop`'s unique keys carry over
 *  - Anything else (non-object JSON, parse failure) → `keep` wins
 *
 * Edges/nodes that were generated from different rooms typically carry
 * JSON like `{ roomFlags: {...} }`; this preserves the union.
 */
export function mergeMetadataStrings(keep: string | null | undefined, drop: string | null | undefined): string | null {
  if (!keep && !drop) return null;
  if (!keep) return drop ?? null;
  if (!drop) return keep;
  try {
    const keepObj = JSON.parse(keep);
    const dropObj = JSON.parse(drop);
    if (keepObj && typeof keepObj === 'object' && !Array.isArray(keepObj) && dropObj && typeof dropObj === 'object' && !Array.isArray(dropObj)) {
      return JSON.stringify({ ...dropObj, ...keepObj });
    }
  } catch {
    // Fall through — one or both sides aren't JSON-object metadata.
  }
  return keep;
}

/**
 * Produce a fresh `SkeletonNodeData` carrying the merged metadata of
 * two nodes. The caller chooses topology fields (`id`, `name`,
 * `position`, `edgeIds`) — this function only fills in the cumulative
 * fields (`roomIds`, `metadata`).
 *
 * Use when:
 *  - The user drags one node onto another (`mergeNodeInto`).
 *  - The auto-pipeline collapses two near-coincident nodes
 *    (`SkeletonProcessingPipeline.mergeNodes` — kept compatible).
 */
export function mergeNodeMetadata(keep: SkeletonNodeData, drop: SkeletonNodeData): { roomIds: string[]; metadata: string | null } {
  return {
    roomIds: unionIds(keep.roomIds, drop.roomIds),
    metadata: mergeMetadataStrings(keep.metadata, drop.metadata),
  };
}

/**
 * Produce a fresh `SkeletonEdgeData` carrying the merged metadata of
 * two edges. Topology (`id`, `name`, `startNodeId`, `endNodeId`) and
 * physical sizes (`leftSize`, `rightSize`, `wallType`) are chosen by
 * the caller; this fills in the cumulative fields.
 *
 * Use when:
 *  - A degree-2 collinear node is deleted/dissolved, fusing its two
 *    incident edges into one through-edge.
 *  - A node-merge dedupes a duplicate edge between the same node pair
 *    (the dropped duplicate's room membership must carry over to the
 *    survivor).
 */
export function mergeEdgeMetadata(keep: SkeletonEdgeData, drop: SkeletonEdgeData): { roomIds: string[]; metadata: string | null } {
  return {
    roomIds: unionIds(keep.roomIds, drop.roomIds),
    metadata: mergeMetadataStrings(keep.metadata, drop.metadata),
  };
}
